import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../../../shared/context/AuthContext'
import { DealListTab } from '../../iso-deal-list'
import DashboardHome from './DashboardHome'
import '../styles/IsoDashboard.css'

const ISODashboard: React.FC = () => {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<'dashboard' | 'deals' | 'submit'>('dashboard')

  // Check for tab parameter in URL on component mount
  useEffect(() => {
    const tabParam = searchParams.get('tab')
    if (tabParam === 'deals' || tabParam === 'submit' || tabParam === 'dashboard') {
      setActiveTab(tabParam)
    }
  }, [searchParams])

  // Update URL when tab changes
  const handleTabChange = (tab: 'dashboard' | 'deals' | 'submit') => {
    setActiveTab(tab)
    setSearchParams({ tab })
  }

  const handleLogout = async () => {
    await logout()
  }

  const handleSwitchToDeals = () => {
    handleTabChange('deals')
  }

  const handleSubmitDeal = () => {
    handleTabChange('submit')
  }

  return (
    <div className="iso-dashboard">
      {/* Left Sidebar Navigation */}
      <div className="iso-sidebar">
        <div className="iso-sidebar-header">
          <div className="iso-brand">
            <span className="iso-brand-icon">🏢</span>
            <span className="iso-brand-text">Partner Portal</span>
          </div>
        </div>
        
        <nav className="iso-sidebar-nav">
          <ul className="iso-nav-list">
            <li className="iso-nav-item">
              <button 
                className={`iso-nav-button ${activeTab === 'dashboard' ? 'active' : ''}`}
                onClick={() => handleTabChange('dashboard')}
                type="button"
              >
                <i className="bi bi-speedometer2 iso-nav-icon"></i>
                <span className="iso-nav-text">Dashboard</span>
              </button>
            </li>
            <li className="iso-nav-item">
              <button 
                className={`iso-nav-button ${activeTab === 'submit' ? 'active' : ''}`}
                onClick={() => handleTabChange('submit')}
                type="button"
              >
                <i className="bi bi-plus-circle iso-nav-icon"></i>
                <span className="iso-nav-text">Submit Deal</span>
              </button>
            </li>
            <li className="iso-nav-item">
              <button 
                className={`iso-nav-button ${activeTab === 'deals' ? 'active' : ''}`}
                onClick={() => handleTabChange('deals')}
                type="button"
              >
                <i className="bi bi-list-ul iso-nav-icon"></i>
                <span className="iso-nav-text">View Deals</span>
              </button>
            </li>
            <li className="iso-nav-item">
              <button 
                className="iso-nav-button"
                onClick={() => navigate('/chatter')}
                type="button"
              >
                <i className="bi bi-chat-dots iso-nav-icon"></i>
                <span className="iso-nav-text">Chatter</span>
              </button>
            </li>
          </ul>
        </nav>

        <div className="iso-sidebar-footer">
          <div className="iso-user-info">
            <div className="iso-user-name">{user?.name}</div>
            <div className="iso-user-role">ISO Partner</div>
          </div>
          <button 
            className="iso-logout-btn"
            onClick={handleLogout}
          >
            <i className="bi bi-box-arrow-right me-2"></i>
            Logout
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="iso-main-content">
        <div className="iso-content-wrapper">

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <DashboardHome 
              onSwitchToDeals={handleSwitchToDeals} 
              onSwitchToSubmit={handleSubmitDeal}
            />
          )}

          {/* Submit Deal Tab */}
          {activeTab === 'submit' && (
            <div 
              className="d-flex flex-column align-items-center justify-content-center"
              style={{ 
                minHeight: '90vh',
                padding: '2rem'
              }}
            >
                <div className="row justify-content-center w-100">
                  <div className="col-lg-6 col-md-8">
                    <div className="card border-0 shadow-lg" style={{ borderRadius: '16px' }}>
                      <div className="card-body text-center p-5">
                        <div className="mb-4">
                          <div 
                            className="rounded-3 d-inline-flex align-items-center justify-content-center mb-3"
                            style={{ 
                              width: '100px', 
                              height: '100px', 
                              background: 'linear-gradient(135deg, var(--color-primary) 0%, var(--color-primary-dark) 100%)',
                              color: 'white'
                            }}
                          >
                            <i className="bi bi-file-earmark-plus" style={{ fontSize: '2.5rem' }}></i>
                          </div>
                        </div>
                        <h3 className="text-primary mb-3">Ready to Submit a New Deal?</h3>
                        <p className="text-muted mb-4 lead">
                          Our comprehensive deal submission form features AI-powered document parsing, 
                          dynamic field mapping, and streamlined file uploads to make the process as smooth as possible.
                        </p>
                        
                        <div className="row text-start mb-4">
                          <div className="col-md-6">
                            <h6 className="text-primary">✨ Features:</h6>
                            <ul className="list-unstyled text-muted">
                              <li><i className="bi bi-check-circle text-success me-2"></i>AI Document Parsing</li>
                              <li><i className="bi bi-check-circle text-success me-2"></i>Auto-fill from PDFs</li>
                              <li><i className="bi bi-check-circle text-success me-2"></i>Dynamic Form Fields</li>
                            </ul>
                          </div>
                          <div className="col-md-6">
                            <h6 className="text-primary">📋 What You'll Need:</h6>
                            <ul className="list-unstyled text-muted">
                              <li><i className="bi bi-file-pdf text-danger me-2"></i>Application Documents</li>
                              <li><i className="bi bi-file-pdf text-danger me-2"></i>Bank Statements</li>
                              <li><i className="bi bi-person text-info me-2"></i>Contact Information</li>
                            </ul>
                          </div>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Bottom Center Button */}
                <div className="mt-4">
                  <button 
                    className="btn btn-primary btn-lg px-5"
                    onClick={() => window.location.href = '/submit-deal'}
                    style={{
                      borderRadius: '50px',
                      fontSize: '1.1rem',
                      fontWeight: '600'
                    }}
                  >
                    <i className="bi bi-plus-circle me-2"></i>
                    Start New Submission
                  </button>
                </div>
            </div>
          )}

          {/* Deals Tab */}
          {activeTab === 'deals' && (
            <div className="iso-content-section">
              <div className="iso-content-header">
                <h2 className="iso-page-title">Your Deals</h2>
              </div>
              <div className="iso-content-body">
                <DealListTab />
              </div>
            </div>
          )}



        </div>
      </div>
    </div>
  )
}

export default ISODashboard